import React, { useState } from "react";
import useBreakpoint from "../../../../utils/useBreakpoint";
import { LandTourDetail } from "../../../../interfaces/land-tour.interface";
import DownloadItineraryButton from "./DownloadItineraryButton";
import GroundTourItineraryImages from "./GroundTourItineraryImages";

interface Props {
  landTour: LandTourDetail;
}

// Day-by-day timeline. Modelled on pages/gd/ItineraryTab.tsx's data shape
// (day/title/description/cities/activities) but written lean - the Figma card
// here is simpler than the series stepper (a static image grid per day, no carousel).
//
// Mobile (Figma 10102:248684/248700/248729) and desktop (10102:260284/260310/
// 260336/260354) are genuinely different arrangements, not just a CSS resize -
// desktop keeps the day pill as a sidebar beside the whole card with the meals
// badge inline next to the toggle, while mobile stacks the pill above the title
// and moves the meals badge to its own full-width row - so the two are branched
// in JS via useBreakpoint (issue #798), the same pattern ChooseYourTour/
// SurchargesModal already use in this folder, rather than forced into one DOM
// shape with CSS alone.
export default function GroundTourItinerary({ landTour }: Props) {
  const { itinerary } = landTour;
  // Empty by default so every day starts expanded.
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const isMobile = useBreakpoint();

  if (!itinerary.length) return null;

  const toggleDay = (index: number) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  return (
    <section className="ground-tour-show-v2__itinerary">
      <div className="ground-tour-show-v2__itinerary-header">
        <h2 className="ground-tour-show-v2__section-title">Itinerary</h2>
        <p>Final sequences of the itinerary is subject to local ground final arrangement.</p>

        <DownloadItineraryButton landTour={landTour} />
      </div>

      <div className="ground-tour-show-v2__day-list">
        {itinerary.map((day, index) => {
          const isExpanded = !collapsed.has(index);

          const toggleButton = (
            <button
              type="button"
              className="ground-tour-show-v2__day-toggle"
              onClick={() => toggleDay(index)}
              aria-label={isExpanded ? "Collapse day" : "Expand day"}
              aria-expanded={isExpanded}
            >
              <img
                className={isExpanded ? "" : "is-collapsed"}
                src="/iceholidays-assets/images/icons/icon-chevron-up.png"
                alt=""
                aria-hidden="true"
              />
            </button>
          );

          const mealsBadge = day.inclusion && (
            <div className="ground-tour-show-v2__day-meals">
              <span className="ground-tour-show-v2__day-meals-icon" aria-hidden="true" />
              <span>{day.inclusion}</span>
            </div>
          );

          const expandedBody = isExpanded && (
            <>
              {day.description && <p className="ground-tour-show-v2__day-description">{day.description}</p>}

              {day.cities.length > 0 && (
                <div className="ground-tour-show-v2__day-cities">
                  <span className="ground-tour-show-v2__day-cities-label">Hotel Check In</span>
                  {day.cities.map((city, cityIndex) => (
                    <div className="ground-tour-show-v2__day-city" key={cityIndex}>
                      <span className="ground-tour-show-v2__day-city-dot" />
                      {city.city}
                    </div>
                  ))}
                </div>
              )}

              {day.activities?.length > 0 && (
                <div className="ground-tour-show-v2__day-activities">
                  <span className="ground-tour-show-v2__day-cities-label">Activities</span>
                  {day.activities.map((activity, activityIndex) => (
                    <div className="ground-tour-show-v2__day-city" key={activityIndex}>
                      <span className="ground-tour-show-v2__day-city-dot" />
                      {activity.activity}
                    </div>
                  ))}
                </div>
              )}

              {day.images?.length > 0 && <GroundTourItineraryImages images={day.images} />}
            </>
          );

          if (isMobile) {
            return (
              <div className="ground-tour-show-v2__day-card" key={index}>
                <div className="ground-tour-show-v2__day-content">
                  <div className="ground-tour-show-v2__day-title-row">
                    <div className="ground-tour-show-v2__day-heading">
                      <span className="ground-tour-show-v2__day-pill">{day.day_no}</span>
                      <h3>{day.title}</h3>
                    </div>
                    {toggleButton}
                  </div>

                  {mealsBadge}
                  {expandedBody}
                </div>
              </div>
            );
          }

          return (
            <div className="ground-tour-show-v2__day-card" key={index}>
              <span className="ground-tour-show-v2__day-pill">{day.day_no}</span>

              <div className="ground-tour-show-v2__day-content">
                <div className="ground-tour-show-v2__day-title-row">
                  <h3>{day.title}</h3>

                  <div className="ground-tour-show-v2__day-actions">
                    {mealsBadge}
                    {toggleButton}
                  </div>
                </div>

                {expandedBody}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
